import React, { useMemo, useState } from 'react';
import { X } from 'lucide-react';
import apiService from '../../services/api';
import { formatARS } from '../../utils/formatCurrency';
import MoneyInput from './MoneyInput';
import useBodyScrollLock from '../../hooks/useBodyScrollLock';
import useEscapeToClose from '../../hooks/useEscapeToClose';

// Says which invoice(s) one payment pays. Ticking an invoice fills the amount by itself: what the
// invoice still lacks, up to what is left of the payment.
const formatDMY = (iso: string) => String(iso || '').slice(0, 10).split('-').reverse().join('/');
const round2 = (value: number) => Math.round(value * 100) / 100;
// Pesos showing the cents only when there are any, so a stray $0,38 does not read as $0.
const formatPesos = (value: number) => `$ ${value.toLocaleString('es-AR', { minimumFractionDigits: Math.round(value * 100) % 100 ? 2 : 0, maximumFractionDigits: 2 })}`;

export const paymentLabel = (p: any) => (p.category === 'cheque'
  ? `Cheque ${p.checkNumber || 's/n'}${p.checkBank ? ` · ${p.checkBank}` : ''}${p.checkDueDate ? ` · vence ${formatDMY(p.checkDueDate)}` : ''}`
  : `${p.category === 'transferencia' ? 'Transferencia' : p.category === 'efectivo' ? 'Efectivo' : 'Pago'} del ${formatDMY(p.movementDate)}`);

const AssignPaymentModal: React.FC<{ payment: any; movements: any[]; onClose: () => void; onSaved: () => void }> = ({ payment, movements, onClose, onSaved }) => {
  useBodyScrollLock(true);
  useEscapeToClose(true, onClose);
  const own: Array<{ invoiceId: number; amount: number }> = payment.allocations || [];
  const [allocations, setAllocations] = useState<Record<number, number>>(() => Object.fromEntries(own.map((a) => [a.invoiceId, a.amount])));
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState('');

  // Invoices with something left to pay (counting this payment's own share as free) or already paid by it, oldest first.
  const invoices = useMemo(() => movements
    .filter((m) => m.movementType === 'documento' && m.category === 'factura')
    .map((m) => ({ m, available: round2(Number(m.unassignedBalance ?? m.amount) + (own.find((a) => a.invoiceId === m.id)?.amount || 0)) }))
    .filter((row) => row.available > 0.01 || allocations[row.m.id] !== undefined)
    .sort((a, b) => String(a.m.movementDate).localeCompare(String(b.m.movementDate)) || a.m.id - b.m.id),
  // eslint-disable-next-line react-hooks/exhaustive-deps
  [movements, payment]);

  const assigned = round2(Object.values(allocations).reduce((sum, v) => sum + (v || 0), 0));
  const left = round2(Number(payment.amount) - assigned);
  const toggle = (invoiceId: number, available: number, checked: boolean) => setAllocations((current) => {
    const next = { ...current };
    if (!checked) { delete next[invoiceId]; return next; }
    const free = Number(payment.amount) - Object.values(current).reduce((sum, v) => sum + (v || 0), 0);
    next[invoiceId] = round2(Math.max(0, Math.min(available, free)));
    return next;
  });

  const save = async () => {
    const list = Object.entries(allocations).filter(([, amount]) => amount > 0).map(([invoiceId, amount]) => ({ invoiceId: Number(invoiceId), amount: round2(amount) }));
    if (assigned > Number(payment.amount) + 0.01) { setError('Lo asignado supera el importe del pago.'); return; }
    const over = list.find((a) => a.amount > (invoices.find((r) => r.m.id === a.invoiceId)?.available || 0) + 0.01);
    if (over) { setError('Uno de los importes supera lo que le falta a la factura.'); return; }
    try {
      setSaving(true); setError('');
      await apiService.updateAccountMovement(payment.id, { allocations: list });
      onSaved(); onClose();
    } catch (e: any) {
      setError(e?.response?.data?.detail || 'No se pudo guardar la asignación.');
    } finally { setSaving(false); }
  };

  return (
    <div className="fixed inset-0 z-50 flex items-end justify-center bg-black/40 sm:items-center sm:p-4" onClick={onClose}>
      <div className="max-h-[92vh] w-full max-w-2xl overflow-y-auto rounded-t-xl bg-white shadow-xl sm:rounded-xl" onClick={(e) => e.stopPropagation()}>
        <div className="flex items-start justify-between gap-3 border-b px-5 py-4">
          <div><h2 className="text-lg font-semibold">¿Qué factura paga?</h2><p className="text-sm text-gray-600">{paymentLabel(payment)} · <strong>{formatARS(payment.amount)}</strong></p></div>
          <button onClick={onClose} aria-label="Cerrar"><X className="h-5 w-5" /></button>
        </div>
        <div className="space-y-3 p-5">
          <p className="text-xs text-gray-500">Tildá la factura o las facturas que paga. El importe se completa solo con lo que le falta a cada una; podés cambiarlo si paga una parte.</p>
          {!invoices.length ? <p className="text-sm text-gray-500">Este cliente no tiene facturas con saldo pendiente.</p> : (
            <div className="divide-y rounded-lg border">
              {invoices.map(({ m, available }) => {
                const checked = allocations[m.id] !== undefined;
                return (
                  <div key={m.id} className={`flex flex-wrap items-center justify-between gap-2 px-3 py-2 text-sm ${checked ? 'bg-blue-50' : ''}`}>
                    <label className="flex cursor-pointer items-center gap-2">
                      <input type="checkbox" checked={checked} onChange={(e) => toggle(m.id, available, e.target.checked)} />
                      <span>Factura {m.invoice?.number || 'sin número'} · {formatDMY(m.movementDate)} · {formatARS(m.amount)}<span className="block text-xs text-gray-500">{(() => { if (!checked) return `le falta ${formatPesos(available)}`; const after = round2(available - (allocations[m.id] || 0)); return Math.abs(after) < 0.01 ? 'con este pago queda cubierta' : after > 0 ? `con este pago le falta ${formatPesos(after)}` : `te pasás ${formatPesos(-after)}`; })()}</span></span>
                    </label>
                    {checked && <span className="w-40"><MoneyInput value={allocations[m.id] || 0} onChange={(v) => setAllocations((cur) => ({ ...cur, [m.id]: v }))} ariaLabel={`Importe para la factura ${m.invoice?.number || m.id}`} className="w-full rounded border px-2 py-1 text-sm" /></span>}
                  </div>
                );
              })}
            </div>
          )}
          <p className={`rounded-lg px-3 py-2 text-sm ${left < -0.004 ? 'bg-red-50 text-red-700' : Math.abs(left) < 0.005 ? 'bg-green-50 text-green-800' : 'bg-amber-50 text-amber-800'}`}>
            Asignado {formatPesos(assigned)} de {formatPesos(Number(payment.amount))}. {left < -0.004 ? `Te pasaste ${formatPesos(-left)}.` : Math.abs(left) < 0.005 ? 'El pago queda asignado completo.' : `Quedan ${formatPesos(left)} sin asignar.`}
          </p>
          {error && <div className="rounded-lg border border-red-200 bg-red-50 p-3 text-sm text-red-700">{error}</div>}
        </div>
        <div className="flex justify-end gap-2 border-t px-5 py-4">
          <button onClick={onClose} className="rounded-lg border px-4 py-2">Cancelar</button>
          <button onClick={save} disabled={saving} className="rounded-lg bg-primary-600 px-4 py-2 text-white disabled:opacity-50">{saving ? 'Guardando...' : 'Guardar'}</button>
        </div>
      </div>
    </div>
  );
};

export default AssignPaymentModal;
